import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useForm, useWatch } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { BASE_URL } from "../../config";

const MAX_VISIBLE_PAGE_BUTTONS = 6;

const formatNumber = (value) => Number(value || 0).toLocaleString();

const buildPageWindow = (currentPage, lastPage) => {
  const safeLastPage = Math.max(1, Number(lastPage || 1));
  const safeCurrentPage = Math.min(Math.max(1, Number(currentPage || 1)), safeLastPage);
  const visibleCount = Math.min(MAX_VISIBLE_PAGE_BUTTONS, safeLastPage);

  let start = Math.max(1, safeCurrentPage - Math.floor(visibleCount / 2));
  let end = start + visibleCount - 1;

  if (end > safeLastPage) {
    end = safeLastPage;
    start = Math.max(1, end - visibleCount + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

const requiredProviderAccessFields = [
  ['corporate', 'Corporate'],
  ['plan', 'Plan'],
  ['coverage', 'Coverage'],
  ['member_type', 'Member Type'],
  ['member_name', 'Member Name'],
  ['prov_type', 'Provider Type'],
  ['provider', 'Provider'],
  ['access_flag', 'Access Flag'],
];

const fieldLabels = requiredProviderAccessFields.reduce((labels, [field, label]) => ({
  ...labels,
  [field]: label,
}), {});

const isEmptyFormValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return true;
  }

  return typeof value === 'object' && !value.value;
};

const getMissingProviderAccessFields = (data) => (
  requiredProviderAccessFields
    .filter(([field]) => isEmptyFormValue(data[field]))
    .map(([, label]) => label)
);

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const flattenErrorMessages = (message) => {
  if (!message) {
    return [];
  }

  if (typeof message === 'string') {
    return [message];
  }

  if (Array.isArray(message)) {
    return message.flatMap(flattenErrorMessages);
  }

  if (typeof message === 'object') {
    return Object.entries(message).flatMap(([field, fieldMessages]) => (
      flattenErrorMessages(fieldMessages).map((fieldMessage) => {
        const label = fieldLabels[field] || field.replace(/_/g, ' ');
        return `${label}: ${fieldMessage}`;
      })
    ));
  }

  return [String(message)];
};

const showProviderAccessError = (title, message, icon = 'error') => {
  const messages = flattenErrorMessages(message);
  const displayMessages = messages.length > 0 ? messages : ['Failed to add access'];

  Swal.fire({
    title,
    icon,
    html: displayMessages.length === 1
      ? `<div class="text-left text-sm">${escapeHtml(displayMessages[0])}</div>`
      : `<ul class="text-left text-sm list-disc pl-5 space-y-1">${displayMessages.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`,
  });
};

const ProviderAccessPage = () => {
  const [selectedCorp, setSelectedCorp] = useState(null);
  const [tableData, setTableData] = useState({ data: [], pagination: {} });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Form state with react-hook-form
  const { handleSubmit, reset, control, setValue } = useForm({
    defaultValues: {
      corporate: null,
      plan: null,
      coverage: null,
      member_name: null,
      provider: null,
      member_type: 'All',
      prov_type: 'both',
      access_flag: 'with',
    },
  });

  // Watch form fields
  const corporate = useWatch({ control, name: 'corporate' });
  const plan = useWatch({ control, name: 'plan' });
  const provType = useWatch({ control, name: 'prov_type' });
  const coverage = useWatch({ control, name: 'coverage' });
  const memberType = useWatch({ control, name: 'member_type' });
  const memberName = useWatch({ control, name: 'member_name' });
  const provider = useWatch({ control, name: 'provider' });
  const accessFlag = useWatch({ control, name: 'access_flag' });

  const [corporates, setCorporates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [coverages, setCoverages] = useState([]);
  const [members, setMembers] = useState([]);
  const [providers, setProviders] = useState([]);

  const records = Array.isArray(tableData.data) ? tableData.data : [];
  const pagination = tableData.pagination || {};
  const totalRecords = Number(pagination.total || 0);
  const totalPages = Math.max(1, Number(pagination.last_page || 1));
  const activePage = Math.min(Math.max(1, Number(pagination.current_page || currentPage)), totalPages);
  const pageNumbers = useMemo(() => buildPageWindow(activePage, totalPages), [activePage, totalPages]);
  const showingFrom = totalRecords === 0 ? 0 : ((activePage - 1) * perPage) + 1;
  const showingTo = totalRecords === 0 ? 0 : Math.min(showingFrom + records.length - 1, totalRecords);
  const canGoPrevious = activePage > 1;
  const canGoNext = activePage < totalPages;

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  // Sync corporate to selectedCorp for table
  useEffect(() => {
    if (corporate && corporate.value) {
      setSelectedCorp(corporate);
      setCurrentPage(1);
      setSearch('');
      setSearchInput('');
    }
  }, [corporate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Load Corporates
  useEffect(() => {
    const loadCorporates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/provider-access/corporates`, {
          headers: getAuthHeaders(),
        });
        setCorporates(response.data.data);
      } catch (error) {
        Swal.fire('Error', 'Failed to load corporates', 'error');
      }
    };
    loadCorporates();
  }, []);

  // Cascade: Plans
  useEffect(() => {
    if (corporate?.value && !isResetting) {
      setIsResetting(true);
      setFormLoading(true);
      axios.get(`${BASE_URL}/provider-access/plans/${corporate.value}`, {
        headers: getAuthHeaders(),
      })
        .then(response => {
          setPlans(response.data.data);
          setValue('plan', null, { shouldValidate: false, shouldDirty: false });
          setCoverages([]);
          setValue('coverage', null, { shouldValidate: false, shouldDirty: false });
          setValue('member_name', null, { shouldValidate: false, shouldDirty: false });
        })
        .catch(() => Swal.fire('Error', 'Failed to load plans', 'error'))
        .finally(() => {
          setFormLoading(false);
          setIsResetting(false);
        });
    }
  }, [corporate, setValue]);

  // Cascade: Coverages
  useEffect(() => {
    if (plan?.value && corporate?.value && !isResetting) {
      setIsResetting(true);
      setFormLoading(true);
      axios.get(`${BASE_URL}/provider-access/coverages/${corporate.value}/${plan.value}`, {
        headers: getAuthHeaders(),
      })
        .then(response => {
          setCoverages(response.data.data);
          setValue('coverage', null, { shouldValidate: false, shouldDirty: false });
        })
        .catch(() => Swal.fire('Error', 'Failed to load coverages', 'error'))
        .finally(() => {
          setFormLoading(false);
          setIsResetting(false);
        });
    }
  }, [plan, corporate, setValue]);

  // Cascade: Members
  useEffect(() => {
    if (corporate?.value && !isResetting) {
      setFormLoading(true);
      axios.get(`${BASE_URL}/provider-access/members/${corporate.value}`, {
        headers: getAuthHeaders(),
      })
        .then(response => {
          setMembers(response.data.data);
        })
        .catch(() => Swal.fire('Error', 'Failed to load members', 'error'))
        .finally(() => setFormLoading(false));
    }
  }, [corporate, setValue]);

  // Cascade: Providers
  useEffect(() => {
    if (provType && !isResetting) {
      setFormLoading(true);
      const typeParam = provType === 'both' ? 'a' : (provType === 'hospital' ? 'h' : 'c');
      axios.get(`${BASE_URL}/provider-access/providers?type=${typeParam}`, {
        headers: getAuthHeaders(),
      })
        .then(response => {
          setProviders(response.data.data);
          setValue('provider', null, { shouldValidate: false, shouldDirty: false });
        })
        .catch(() => Swal.fire('Error', 'Failed to load providers', 'error'))
        .finally(() => setFormLoading(false));
    }
  }, [provType, setValue]);

  // Fetch Table Data
  const fetchTableData = useCallback(async (corpCode, searchVal = '', perPageVal = 10, pageVal = 1) => {
    if (!corpCode) return; // Guard against undefined
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/provider-access/list/${corpCode}`, {
        headers: getAuthHeaders(),
        params: { search: searchVal || '', per_page: perPageVal, page: pageVal },
      });
      setTableData(response.data);
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCorp?.value) {
      fetchTableData(selectedCorp.value, search, perPage, currentPage);
    } else {
      setTableData({ data: [], pagination: {} }); // Clear on no corp
    }
  }, [selectedCorp, search, perPage, currentPage, fetchTableData]);

  // Form Submit
  const onSubmit = useCallback(async (data) => {
    const missingFields = getMissingProviderAccessFields(data);

    if (missingFields.length > 0) {
      showProviderAccessError(
        'Missing Required Fields',
        missingFields.map((field) => `${field} is required.`),
        'warning'
      );
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        corp_code: data.corporate.value,
        plan_code: data.plan.value,
        coverage_code: data.coverage.value,
        provider_type: data.prov_type === 'both' ? 'a' : (data.prov_type === 'hospital' ? 'h' : 'c'),
        provider_code: data.provider.value,
        member_type: data.member_type,
        member_id: data.member_name.value,
        access_flag: data.access_flag === 'with' ? 'Y' : 'X',
      };

      const response = await axios.post(`${BASE_URL}/provider-access/add`, payload, {
        headers: getAuthHeaders(),
      });
      if (response.data.status === 'success') {
        Swal.fire('Success', response.data.message, 'success');
        reset();
        setSelectedCorp(null); // Reset to re-select
        setSearch(''); // Clear search
        setSearchInput('');
      } else {
        showProviderAccessError('Failed to add access', response.data.message);
      }
    } catch (error) {
      showProviderAccessError(
        'Failed to add access',
        error.response?.data?.message || error.response?.data?.errors || error.message || 'Failed to add access'
      );
    } finally {
      setFormLoading(false);
    }
  }, [reset]);

  // Delete Handler
  const handleDelete = useCallback((tblId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This will permanently delete the provider access!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(`${BASE_URL}/provider-access/delete/${tblId}`, {
          headers: getAuthHeaders(),
        })
          .then(() => {
            Swal.fire('Deleted!', 'Provider access removed.', 'success');
            if (selectedCorp?.value) {
              fetchTableData(selectedCorp.value, search, perPage, currentPage);
            }
          })
          .catch((error) => {
            Swal.fire('Error', error.response?.data?.message || 'Failed to delete', 'error');
          });
      }
    });
  }, [fetchTableData, selectedCorp, search, perPage, currentPage]);

  // Search Handler (debounced)
  const handleSearch = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  // Per Page Handler
  const handlePerPageChange = useCallback((e) => {
    setPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  }, []);

  // Page Change Handler
  const handlePageChange = useCallback((page) => {
    const nextPage = Math.min(Math.max(1, Number(page) || 1), totalPages);
    setCurrentPage(nextPage);
  }, [totalPages]);

  // Static Options
  const memberTypeOptions = [
    { value: 'All', label: 'All' },
    { value: 'D', label: 'Dependent' },
    { value: 'P', label: 'Principal' },
  ];

  const providerTypeOptions = [
    { id: 'both', value: 'both', label: 'Both' },
    { id: 'hospital', value: 'hospital', label: 'Hospital' },
    { id: 'clinic', value: 'clinic', label: 'Clinic' },
  ];

  const accessFlagOptions = [
    { id: 'with', value: 'with', label: 'With Access' },
    { id: 'no', value: 'no', label: 'No Access (Blocked)' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Maintenance</p>
            <h1 className="mt-1 text-2xl font-bold text-slate-950 sm:text-3xl">Provider Access</h1>
          </div>
          {selectedCorp && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800">
              {selectedCorp.label}
            </div>
          )}
        </div>

      {/* Form Section */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Add Provider Access</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* ... (Selects and radios unchanged from previous - with [code] labels now from API) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Corporate *</label>
            <Select
              options={corporates}
              value={corporate}
              onChange={(val) => setValue('corporate', val, { shouldValidate: true })}
              isSearchable
              placeholder="Select Corporate"
              isLoading={formLoading}
              className="basic-select"
              classNamePrefix="select"
              isDisabled={formLoading}
            />
          </div>
          {/* Plan, Coverage, Member Type, Member Name, Provider Type, Provider, Access Flag - same as before */}
          {/* Submit Button - same */}

          {/* Plan */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Plan *</label>
                      <Select
                        options={plans}
                        value={plan}
                        onChange={(val) => setValue('plan', val, { shouldValidate: true })}
                        isSearchable
                        placeholder="Select Plan"
                        isLoading={formLoading}
                        isDisabled={!corporate || formLoading}
                        className="basic-select"
                        classNamePrefix="select"
                      />
                    </div>
          
                    {/* Coverage */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Coverage *</label>
                      <Select
                        options={coverages}
                        value={coverage}
                        onChange={(val) => setValue('coverage', val, { shouldValidate: true })}
                        isSearchable
                        placeholder="Select Coverage"
                        isLoading={formLoading}
                        isDisabled={!plan || formLoading}
                        className="basic-select"
                        classNamePrefix="select"
                      />
                    </div>
          
                    {/* Member Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Member Type *</label>
                      <Select
                        options={memberTypeOptions}
                        value={memberTypeOptions.find(opt => opt.value === memberType) || null}
                        onChange={(val) => setValue('member_type', val ? val.value : 'All', { shouldValidate: true })}
                        isSearchable={false}
                        placeholder="Select Member Type"
                        isLoading={formLoading}
                        className="basic-select"
                        classNamePrefix="select"
                      />
                    </div>
          
                    {/* Member Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Member Name *</label>
                      <Select
                        options={members}
                        value={memberName}
                        onChange={(val) => setValue('member_name', val, { shouldValidate: true })}
                        isSearchable
                        placeholder="Select Member Name"
                        isDisabled={!corporate || formLoading}
                        isLoading={formLoading}
                        className="basic-select"
                        classNamePrefix="select"
                      />
                    </div>
          
                    {/* Provider Type */}
                    <div className="md:col-span-2 lg:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Provider Type *</label>
                      <div className="flex flex-wrap gap-3">
                        {providerTypeOptions.map((opt) => (
                          <label key={opt.id} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              value={opt.value}
                              checked={provType === opt.value}
                              onChange={(e) => setValue('prov_type', e.target.value, { shouldValidate: true })}
                              className="form-radio text-blue-600 h-4 w-4"
                            />
                            <span className="text-sm text-gray-700">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
          
                    {/* Provider */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Provider *</label>
                      <Select
                        options={providers}
                        value={provider}
                        onChange={(val) => setValue('provider', val, { shouldValidate: true })}
                        isSearchable
                        placeholder="Select Provider"
                        isLoading={formLoading}
                        isDisabled={!provType || formLoading}
                        className="basic-select"
                        classNamePrefix="select"
                      />
                    </div>
          
                    {/* Access Flag */}
                    <div className="md:col-span-2 lg:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Access Flag *</label>
                      <div className="flex flex-wrap gap-3">
                        {accessFlagOptions.map((opt) => (
                          <label key={opt.id} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              value={opt.value}
                              checked={accessFlag === opt.value}
                              onChange={(e) => setValue('access_flag', e.target.value, { shouldValidate: true })}
                              className="form-radio text-green-600 h-4 w-4"
                            />
                            <span className="text-sm text-gray-700">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
          
                    {/* Submit Button */}
                    <div className="md:col-span-2 lg:col-span-3 flex justify-end">
                      <button
                        type="submit"
                        disabled={formLoading}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-bold py-2 px-6 rounded-lg shadow-md transition duration-200 flex items-center space-x-2"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{formLoading ? 'Adding...' : 'Add Provider Access'}</span>
                        {formLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                      </button>
                    </div>

        </form>
      </div>

        {/* Table Section */}
        {selectedCorp && (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-4 border-b border-slate-200 p-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Provider Access List</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {formatNumber(totalRecords)} records
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  type="text"
                  placeholder="Search provider access..."
                  value={searchInput}
                  onChange={handleSearch}
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 sm:w-72"
                />
                <select
                  value={perPage}
                  onChange={handlePerPageChange}
                  className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-[1180px] divide-y divide-slate-200">
                <thead className="bg-slate-100">
                  <tr>
                    {['Corp Name', 'Plan Name', 'Coverage', 'Member Type', 'Member Name', 'Provider Type', 'Provider', 'Access Flag', 'Actions'].map((header) => (
                      <th key={header} className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-600">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-12 text-center">
                        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-emerald-100 border-t-emerald-600"></div>
                        <p className="mt-3 text-sm font-medium text-slate-500">Loading data...</p>
                      </td>
                    </tr>
                  ) : records.length > 0 ? (
                    records.map((row) => (
                      <tr key={row.tbl_id} className="transition hover:bg-emerald-50/40">
                        <td className="px-4 py-3 text-sm font-semibold text-slate-900">{row.corp_name || row.corp_code || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{row.plan_name || row.plan_code || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{row.coverage_name || row.coverage_code || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">
                          <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                            {row.member_type || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">{row.member_name || row.member_id || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{row.provider_type || 'N/A'}</td>
                        <td className="max-w-[300px] whitespace-normal px-4 py-3 text-sm text-slate-700">{row.provider_name || row.provider_code || 'N/A'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${
                            row.access_flag === 'Y'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-rose-100 text-rose-800'
                          }`}>
                            {row.access_flag_label || (row.access_flag === 'Y' ? 'With Access' : 'Blocked')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">
                          <button
                            type="button"
                            onClick={() => handleDelete(row.tbl_id)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-200 bg-white text-rose-600 transition hover:bg-rose-50 hover:text-rose-700"
                            title="Delete"
                            aria-label="Delete provider access"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="px-6 py-12 text-center">
                        <p className="text-sm font-semibold text-slate-700">No provider access records found.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="border-t border-slate-200 bg-slate-50 px-5 py-4">
                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                  <div className="text-sm text-slate-600">
                    Showing <span className="font-semibold text-slate-900">{formatNumber(showingFrom)}</span>
                    {' - '}
                    <span className="font-semibold text-slate-900">{formatNumber(showingTo)}</span>
                    {' of '}
                    <span className="font-semibold text-slate-900">{formatNumber(totalRecords)}</span> entries
                  </div>
                  <nav className="flex flex-wrap items-center gap-1.5" aria-label="Provider access pagination">
                    <button
                      type="button"
                      onClick={() => handlePageChange(1)}
                      disabled={!canGoPrevious || loading}
                      className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      First
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePageChange(activePage - 1)}
                      disabled={!canGoPrevious || loading}
                      className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Previous
                    </button>
                    {pageNumbers.map((page) => (
                      <button
                        type="button"
                        key={page}
                        onClick={() => handlePageChange(page)}
                        disabled={loading}
                        className={`h-9 min-w-9 rounded-lg px-3 text-sm font-bold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                          page === activePage
                            ? 'bg-emerald-600 text-white shadow-sm'
                            : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => handlePageChange(activePage + 1)}
                      disabled={!canGoNext || loading}
                      className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={!canGoNext || loading}
                      className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Last
                    </button>
                  </nav>
                </div>
              </div>
            )}
          </div>
        )}

        {!selectedCorp && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm font-medium text-slate-500">
            Select a Corporate above to view provider access list.
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderAccessPage;
